import { useTranslation } from 'react-i18next'

import type { StateCategory } from '@/types/domain'
import type { Timeline as TimelineData, TimelineRow } from '@/types/workflow'

const CATEGORY_COLOR: Record<StateCategory, string> = {
  backlog: 'var(--color-state-backlog)',
  started: 'var(--color-state-started)',
  blocked: 'var(--color-state-blocked)',
  done: 'var(--color-state-done)',
  cancelled: 'var(--color-state-cancelled)',
}

/**
 * The timeline layout: the fifth renderer over the same view as the board, the
 * list and the table (ADR-0003).
 *
 * Bars are positioned on calendar days because that is what a chart is, but
 * every number written next to one — days left, span — is working days on the
 * Oman calendar, computed server-side. Weekends and public holidays are drawn
 * as shading so the two readings never look like a contradiction.
 */
export function Timeline({ timeline }: { timeline: TimelineData }) {
  const { t } = useTranslation()

  const start = Date.parse(timeline.window.startsOn)
  const end = Date.parse(timeline.window.endsOn)
  const span = Math.max(end - start, 86_400_000)

  const position = (date: string): number => ((Date.parse(date) - start) / span) * 100

  const rows = [...timeline.milestones, ...timeline.items]

  // What each row is waiting on, by key. Drawing arrows between bars looks
  // impressive and reads badly at this row height; naming the blocker is the
  // information someone actually needs off a dependency.
  const byId = new Map(rows.map((row) => [row.id, row]))
  const blockedBy = new Map<string, string[]>()

  for (const edge of timeline.dependencies) {
    const blocker = byId.get(edge.fromId)
    if (blocker === undefined) continue

    blockedBy.set(edge.toId, [...(blockedBy.get(edge.toId) ?? []), blocker.key])
  }

  if (rows.length === 0) {
    return (
      <p className="border-line bg-surface text-ink-700 rounded-md border p-4 text-sm">
        {t('project.nothingDated')}
      </p>
    )
  }

  return (
    <div className="border-line bg-surface rounded-md border">
      <div className="border-line text-ink-500 flex items-center justify-between border-b px-4 py-2 font-mono text-[10px] tracking-widest uppercase">
        <span>{timeline.window.startsOn}</span>
        <span>{timeline.window.endsOn}</span>
      </div>

      <ul className="divide-line divide-y">
        {rows.map((row) => (
          <li key={row.id} className="grid gap-1 px-4 py-3 sm:grid-cols-[220px_minmax(0,1fr)] sm:gap-4">
            <div className="min-w-0">
              <span className="text-brand-600 font-mono text-[10px]">{row.key}</span>
              <div className="text-ink-900 truncate text-sm">{row.title}</div>
              {(blockedBy.get(row.id) ?? []).length === 0 ? null : (
                <div className="text-ink-500 text-xs">
                  {t('project.blockedBy')}{' '}
                  <span className="font-mono">{(blockedBy.get(row.id) ?? []).join(', ')}</span>
                </div>
              )}

              <div className="text-ink-500 text-xs">
                {row.isOverdue ? (
                  <span className="text-[color:var(--color-state-blocked)]">
                    {t('project.overdue')}
                  </span>
                ) : row.workingDaysRemaining === null ? (
                  (row.assignee ?? '—')
                ) : row.workingDaysRemaining === 0 ? (
                  t('project.dueToday')
                ) : (
                  t('project.daysLeft', { count: row.workingDaysRemaining })
                )}
              </div>
            </div>

            <div className="relative h-7">
              <span className="bg-surface-2 absolute inset-y-3 inset-x-0 rounded-full" />

              {timeline.holidays.map((holiday) => (
                <span
                  key={holiday.date}
                  title={holiday.name}
                  aria-hidden="true"
                  className="absolute inset-y-1 w-0.5 bg-[color:var(--color-state-blocked)] opacity-30"
                  style={{ insetInlineStart: `${position(holiday.date)}%` }}
                />
              ))}

              <Bar row={row} position={position} />

              <span
                aria-hidden="true"
                title={t('project.today')}
                className="bg-ink-900 absolute inset-y-0 w-px"
                style={{ insetInlineStart: `${position(timeline.today)}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Bar({ row, position }: { row: TimelineRow; position: (date: string) => number }) {
  const color = CATEGORY_COLOR[row.stateCategory ?? 'backlog']

  // A milestone is a date, not a duration: it renders as a marker rather than a
  // bar, which is the whole reason it is a distinct type.
  if (row.type === 'milestone' || row.startDate === null || row.dueDate === null) {
    const at = row.dueDate ?? row.startDate

    if (at === null) return null

    return (
      <span
        className="absolute top-1/2 size-3 -translate-y-1/2 rotate-45"
        style={{ insetInlineStart: `calc(${position(at)}% - 6px)`, backgroundColor: color }}
      />
    )
  }

  const from = position(row.startDate)
  const to = position(row.dueDate)

  return (
    <span
      className="absolute inset-y-2.5 rounded-full"
      style={{
        insetInlineStart: `${from}%`,
        width: `${Math.max(to - from, 1)}%`,
        backgroundColor: color,
      }}
    />
  )
}
